import { legacyColors, greyColors, primaryColors, successColors, warningColors } from "theme/colors";

import { ParticipantType } from "utils/types/employee-journey";
import { PuzzleType } from "utils/validations/employee-journey/mini-game/mini-game";
import { QuizPuzzle } from "utils/validations/employee-journey/mini-game/quiz";
import { WhoIsItPuzzle } from "utils/validations/employee-journey/mini-game/who-is-it";
import { CrosswordPuzzle } from "utils/validations/mini-game/crossword";
import { MatchImagesPuzzle } from "utils/validations/employee-journey/mini-game/match-images";
import { OrderThisPuzzle } from "utils/validations/employee-journey/mini-game/order-this";
import { FillInTheGapPuzzle } from "utils/validations/employee-journey/mini-game/fill-in-the-gap";
import { WordSearchPuzzle } from "utils/validations/employee-journey/mini-game/word-search";

// * COPIED FROM THE BACKEND

export enum ActivityStatus {
	LOCKED = "locked",
	OVERDUE = "overdue",
	SOON = "soon",
	READY = "ready",
	IN_PROGRESS = "in_progress",
	COMPLETED = "completed",
	REJECTED = "rejected",
	FAILED = "failed",
	WAITING_FOR_APPROVAL = "waiting_for_approval",
}

export enum RouteStatus {
	COMPLETE = "done",
	READY = "ready",
	IN_PROGRESS = "in_progress",
	SOON = "soon",
	OVERDUE = "overdue",
	LOCKED = "locked",
}

export enum JourneyActivityType {
	STORY = "story",
	MEETING = "meeting",
	EVENT = "event",
	UPLOAD_FILE = "upload_file",
	SETUP_GOAL = "setup_goal",
	GOAL = "goal",
	BUDDY = "buddy", // Dummy activity type to show Buddy questionnaire
	QUESTION = "questionnaire",
	TASK = "task",
	MINI_GAME = "mini_game",
}

interface BaseActivityResponse {
	activityId: string;
	title: string;
	description?: string;
	status: ActivityStatus;
	dueDate: Date;
	lockedUntilDate?: Date;
	lockedBy: {
		routeId: string;
		activityId: string;
		name: string;
		type: JourneyActivityType;
	}[];
}
export interface UploadFileActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.UPLOAD_FILE;

	templateFileUrl: string;
	filename: string;
	userDocumentId: string;
	acceptFileType: string;
}
export interface StoryActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.STORY;
	room: {
		id: string;
		roomId: string;
		name: string;
		durationMS: number;
		completionRate: number;
	};
}
export interface MeetingActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.MEETING;

	location: string;
	startTime: string;
	endTime: string;
	participants: {
		email: string;
		name: string;
	}[];
	icsBody: string;
}

export interface EventActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.EVENT;
	location: string;
	startTime: string;
	endTime: string;
	icsBody: string;
	hosts: {
		name: string;
		email: string;
	}[];
}

export interface GoalActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.SETUP_GOAL | JourneyActivityType.GOAL;
	needApproval: boolean;
}

/**
 * Dummy activity type to show Buddy questionnaire. FE only
 */
export interface BuddyActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.BUDDY;
}

export interface QuestionActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.QUESTION;
	userQuestionnaireId: string;
	questionnaireId: string;
	totalQuestion: number;
	isCompleted: boolean;
	editableUntilDate: string;
}

interface Recipient {
	name: string;
	email: string;
	participantType: ParticipantType;
}

export interface TaskActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.TASK;
	journeyActivityTemplateId: string;
	taskId: string;
	userTaskId: string;
	taskTime: string;
	recipients: Recipient[];
	dueAt?: string;
	user?: {
		_id: string;
		name: string;
		email: string;
		profilePictureUrl: string;
	};
}

export interface QuizPuzzleResponse extends QuizPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface WhoIsItPuzzleResponse extends WhoIsItPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface CrosswordPuzzleResponse extends CrosswordPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface MatchImagesPuzzleResponse extends MatchImagesPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface OrderThisPuzzleResponse extends OrderThisPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface FillInTheGapPuzzleResponse extends FillInTheGapPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface WordSearchPuzzleResponse extends WordSearchPuzzle {
	_id: string;
	organizationId: string;
	userId: string;
	miniGameId: string;
}

export interface PuzzleActivityResponse extends BaseActivityResponse {
	type: JourneyActivityType.MINI_GAME;
	journeyActivityTemplateId: string;
	userMinigameId: string;
	miniGameId: string;
	miniGameType: PuzzleType;
	score: number;
}

export type UserMinigame =
	| QuizPuzzleResponse
	| WhoIsItPuzzleResponse
	| CrosswordPuzzleResponse
	| MatchImagesPuzzleResponse
	| OrderThisPuzzleResponse
	| FillInTheGapPuzzleResponse
	| WordSearchPuzzleResponse;

export type UserJourneyActivityResponse =
	| UploadFileActivityResponse
	| StoryActivityResponse
	| MeetingActivityResponse
	| EventActivityResponse
	| GoalActivityResponse
	| BuddyActivityResponse
	| QuestionActivityResponse
	| TaskActivityResponse
	| PuzzleActivityResponse;

export interface UserJourneyRouteResponse {
	id: string;
	name: string;
	description?: string;
	iconUrl: string;
	status: RouteStatus;
	activities: UserJourneyActivityResponse[];
}

export const statusBgColor = {
	[ActivityStatus.LOCKED]: greyColors["100"],
	[ActivityStatus.IN_PROGRESS]: primaryColors["50"],
	[ActivityStatus.READY]: primaryColors["50"],
	[ActivityStatus.COMPLETED]: successColors["50"],
	[ActivityStatus.FAILED]: legacyColors.secondary["100"],
	[ActivityStatus.OVERDUE]: legacyColors.secondary["100"],
	[ActivityStatus.REJECTED]: legacyColors.secondary["100"],
	[ActivityStatus.SOON]: warningColors["50"],
};

export const statusBorderColor = {
	[ActivityStatus.LOCKED]: greyColors["200"],
	[ActivityStatus.IN_PROGRESS]: primaryColors["200"],
	[ActivityStatus.READY]: primaryColors["200"],
	[ActivityStatus.COMPLETED]: successColors["200"],
	[ActivityStatus.FAILED]: legacyColors.secondary["200"],
	[ActivityStatus.OVERDUE]: legacyColors.secondary["200"],
	[ActivityStatus.REJECTED]: legacyColors.secondary["200"],
	[ActivityStatus.SOON]: warningColors["200"],
	[ActivityStatus.WAITING_FOR_APPROVAL]: legacyColors.primary["200"],
};

export const progressbarColor = {
	[ActivityStatus.LOCKED]: greyColors["600"],
	[ActivityStatus.IN_PROGRESS]: primaryColors.main,
	[ActivityStatus.READY]: primaryColors.main,
	[ActivityStatus.COMPLETED]: successColors.main,
	[ActivityStatus.FAILED]: legacyColors.secondary["400"],
	[ActivityStatus.OVERDUE]: legacyColors.secondary["400"],
	[ActivityStatus.REJECTED]: legacyColors.secondary["400"],
	[ActivityStatus.SOON]: warningColors.main,
	[ActivityStatus.WAITING_FOR_APPROVAL]: "#36454F",
};

export const iconColor = {
	[ActivityStatus.LOCKED]: greyColors["500"],
	[ActivityStatus.IN_PROGRESS]: primaryColors["300"],
	[ActivityStatus.READY]: primaryColors["300"],
	[ActivityStatus.COMPLETED]: successColors["300"],
	[ActivityStatus.FAILED]: legacyColors.secondary["300"],
	[ActivityStatus.OVERDUE]: legacyColors.secondary["300"],
	[ActivityStatus.REJECTED]: legacyColors.secondary["300"],
	[ActivityStatus.SOON]: warningColors["300"],
	[ActivityStatus.WAITING_FOR_APPROVAL]: legacyColors.primary["300"],
};
